// The header of a client's record: safety banner, identity, the four facts a coordinator looks up,
// the primary contact, and the record's tabs. It is the only navigation for the record.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle, ArchiveRestore, Archive, CalendarPlus, ChevronDown, ChevronLeft, Download, FileText,
  History, Loader2, Pencil, Phone, Sparkles,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useFeatures } from '../../context/FeatureContext';
import {
  Dialog, MicroLabel, OverflowMenu, PersonAvatar, StatRow, StatusPill, Timeline, ErrorState,
  clientTypeLabel, fmtDate, money,
} from '../../components/ui';
import {
  ageOf, dayOf, daysUntil, downloadCsv, formatAddress, fullName, genderLabel, primaryContactOf,
} from './clientShared';

const TABS = [
  { label: 'Overview', path: '' },
  { label: 'Care plan', path: '/care-plan' },
  { label: 'Calendar', path: '/calendar' },
  { label: 'Documents', path: '/documents' },
  { label: 'Goals', path: '/goals' },
  { label: 'Billing', path: '/billing-report' },
  { label: 'Charts', path: '/charts' },
  { label: 'Communications', path: '/communications' },
  { label: 'Email', path: '/email' },
];
// Pages reached less often live under 'More' (they used to be only in the sidebar).
const MORE = [
  { label: 'Quotes', path: '/quotes', to: '/quotes/new', feature: 'enable_quotes' },
  { label: 'Forms', path: '/forms' },
  { label: 'Family portal', path: '/family-portal' },
];

const TAB_LINK = '-mb-px inline-flex shrink-0 items-center border-b-2 px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-150';

function HeaderSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" aria-busy="true" aria-label="Loading client">
      <div className="skeleton h-4 w-24" />
      <div className="flex items-center gap-4 mt-4">
        <div className="skeleton w-12 h-12 !rounded-full" />
        <div className="space-y-2 flex-1">
          <div className="skeleton h-5 w-56" />
          <div className="skeleton h-4 w-40" />
        </div>
      </div>
      <div className="skeleton h-16 w-full mt-4" />
      <div className="flex gap-4 mt-5">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-4 w-20" />)}</div>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <MicroLabel>{label}</MicroLabel>
      <div className="mt-1 text-[15px] font-semibold text-slate-900 tabular-nums truncate">{children}</div>
    </div>
  );
}

const NotSet = () => <span className="text-sm font-normal text-slate-500">Not set</span>;

// The client's change history (moved here from the bottom of the Overview).
function HistoryDialog({ clientId, open, onClose }) {
  const [state, setState] = useState({ loading: false, error: null, rows: null });
  const load = React.useCallback(() => {
    setState({ loading: true, error: null, rows: null });
    api.get(`/clients/${clientId}/audit-log`)
      .then((r) => setState({ loading: false, error: null, rows: Array.isArray(r.data) ? r.data : [] }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load the history.', rows: null }));
  }, [clientId]);
  React.useEffect(() => { if (open) load(); }, [open, load]);
  const items = (state.rows || []).map((e) => ({
    id: e.id, actor: e.changed_by_name || 'System',
    action: e.action === 'update' ? 'updated the profile' : String(e.action || '').replace(/_/g, ' '),
    at: e.created_at,
  }));
  return (
    <Dialog open={open} onClose={onClose} title="History" description="Who changed this profile, and when." size="md">
      {state.loading && <div className="space-y-3 py-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-8" />)}</div>}
      {state.error && <ErrorState message={state.error} onRetry={load} />}
      {state.rows && <Timeline items={items} emptyText="No changes recorded yet. Edits to this profile will appear here." />}
    </Dialog>
  );
}

export default function ClientHeader({ client, riskScore, loading, onClientUpdated }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const features = useFeatures() || {};
  const fileInputRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // How this client's SIL is claimed (Funding > the SIL line): shown beside the status, so the
  // claiming method is visible without opening the funding plan. Quietly absent for roles that
  // cannot read funding.
  const [sil, setSil] = useState(null);
  useEffect(() => {
    if (!client?.id) return undefined;
    let live = true;
    api.get(`/sil-weekly/lines?clientId=${client.id}`).then(({ data }) => {
      if (!live || !Array.isArray(data) || !data.length) return;
      const today = new Date().toISOString().slice(0, 10);
      // Only SIL funding that covers today counts: an ended SIL line says nothing about now.
      const current = data.filter((l) => (!l.periodFrom || l.periodFrom <= today) && (!l.periodTo || l.periodTo >= today));
      const weekly = current.find((l) => l.weeklyFrom && (!l.weeklyTo || l.weeklyTo >= today));
      setSil(weekly ? { method: 'weekly', from: weekly.weeklyFrom, amount: weekly.schedule?.currentWeekly } : current.length ? { method: 'hourly' } : null);
    }).catch(() => {});
    return () => { live = false; };
  }, [client?.id]);

  const base = client ? `/clients/${client.id}` : '';
  const more = MORE.filter((t) => !t.feature || features[t.feature] !== false);
  const moreActive = client ? more.find((t) => pathname.startsWith(base + t.path)) : null;
  const moreLabel = moreActive ? moreActive.label : 'More';
  // The 'More' menu's trigger reads like a tab (and names the page when one of its pages is open).
  const MoreTrigger = useMemo(() => function MoreTrigger() {
    return (
      <span className={clsx(TAB_LINK, moreActive ? 'border-primary-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300')}>
        {moreLabel}<ChevronDown size={14} className="ml-1" aria-hidden />
      </span>
    );
  }, [moreActive, moreLabel]);

  if (!client) return loading === false ? null : <HeaderSkeleton />;

  const name = fullName(client);
  const age = ageOf(client.date_of_birth);
  const review = daysUntil(client.next_review_date);
  // Same sums as before; only the formatting changed (to the cent).
  const totalFunds = (client.funds || []).reduce((s, f) => s + Number(f.fund_amount || 0), 0);
  const totalBalance = (client.funds || []).reduce((s, f) => s + Number(f.fund_amount || 0) - Number(f.spent_amount || 0), 0);
  const primary = primaryContactOf(client);
  const archived = client.is_active === false;

  async function archiveClient() {
    if (!await confirm(`${name} leaves the active lists. Their records are kept and can be restored.`, {
      title: `Archive ${name}?`, danger: true, confirmLabel: 'Archive client',
    })) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success(`${name} archived`);
      navigate('/clients');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not archive this client'); }
  }

  async function restoreClient() {
    if (!await confirm(`${name} goes back on the active clients list.`, { title: `Restore ${name}?`, confirmLabel: 'Restore client' })) return;
    try {
      await api.patch(`/clients/${client.id}/restore`);
      toast.success(`${name} restored`);
      if (onClientUpdated) onClientUpdated();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not restore this client'); }
  }

  function downloadProfile() {
    downloadCsv(`client-${client.first_name || ''}-${client.last_name || ''}.csv`.replace(/\s+/g, '-'), [
      ['Field', 'Value'],
      ['Name', name],
      ['NDIS number', client.ndis_number || ''],
      ['Date of birth', dayOf(client.date_of_birth)],
      ['Gender', genderLabel(client.gender)],
      ['Plan management', client.client_type ? clientTypeLabel(client.client_type) : ''],
      ['Mobile', client.mobile || ''],
      ['Primary contact', primary ? `${primary.first_name || ''} ${primary.last_name || ''}`.trim() : ''],
      ['Contact phone', primary?.phone || ''],
      ['Contact email', primary?.email || ''],
      ['Address', formatAddress(client)],
      ['Next review', dayOf(client.next_review_date)],
      ['Plan period', [dayOf(client.plan_period_start), dayOf(client.plan_period_end)].filter(Boolean).join(' to ')],
    ]);
  }

  const handleAiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 15 * 1024 * 1024) { toast.error('That file is over 15 MB. Choose a smaller one.'); return; }
    setExtracting(true);
    const toastId = toast.loading('Reading the document…');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/clients/${client.id}/auto-fill`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const bits = [];
      if (data.filledFields?.length) bits.push(`${data.filledFields.length} field${data.filledFields.length === 1 ? '' : 's'}`);
      if (data.addedRisks) bits.push(`${data.addedRisks} risk${data.addedRisks === 1 ? '' : 's'}`);
      if (data.addedGoals) bits.push(`${data.addedGoals} goal${data.addedGoals === 1 ? '' : 's'}`);
      if (data.addedContacts) bits.push(`${data.addedContacts} contact${data.addedContacts === 1 ? '' : 's'}`);
      toast.success(bits.length ? `Filled from the document: ${bits.join(' · ')}` : 'Nothing new found in that document', { id: toastId, duration: 5000 });
      if (onClientUpdated) onClientUpdated(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read that document', { id: toastId });
    } finally {
      setExtracting(false);
    }
  };

  const menu = [
    // Off when the participant withdrew AI-processing consent, as on the Documents tab (the server
    // refuses it too).
    { label: extracting ? 'Reading document…' : 'Fill from document', icon: Sparkles, disabled: extracting || client.ai_processing_consent === false, hint: client.ai_processing_consent === false ? 'Consent off' : undefined, onClick: () => fileInputRef.current?.click() },
    features.enable_quotes !== false && { label: 'New quote', icon: FileText, onClick: () => navigate(`${base}/quotes/new`) },
    { label: 'Export profile (CSV)', icon: Download, onClick: downloadProfile },
    { label: 'History', icon: History, onClick: () => setHistoryOpen(true) },
    { divider: true },
    archived
      ? { label: 'Restore client', icon: ArchiveRestore, onClick: restoreClient }
      : { label: 'Archive client', icon: Archive, danger: true, onClick: archiveClient },
  ];

  const isTabActive = (t) => (t.path === ''
    ? pathname === base || pathname === `${base}/details`
    : pathname === base + t.path || pathname.startsWith(`${base + t.path}/`));

  return (
    <header className="bg-white border border-slate-200 rounded-xl print:border-0">
      {/* Safety first: risk alert and allergies always show above everything else. */}
      {(client.risk_alert || client.allergies || client.risk_notification) && (
        <div className="rounded-t-xl px-4 sm:px-5 py-3 bg-rose-50 border-b border-rose-200" role="note" aria-label="Safety information">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-rose-500 shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm text-rose-800 space-y-0.5 min-w-0">
              {client.risk_alert && <p><span className="font-semibold">Risk alert:</span> {client.risk_alert}</p>}
              {client.risk_notification && <p>{client.risk_notification}</p>}
              {client.allergies && <p><span className="font-semibold">Allergies:</span> {client.allergies}</p>}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-5 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Link to="/clients" className="inline-flex items-center gap-1 h-9 -ml-1 px-1 rounded-lg text-[13px] font-medium text-slate-500 hover:text-slate-800">
            <ChevronLeft size={15} aria-hidden /> Clients
          </Link>
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,image/*" onChange={handleAiUpload} />
            {extracting && <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Reading document" />}
            <button type="button" onClick={() => navigate(`/scheduler?clientId=${client.id}`)} className="btn-primary">
              <CalendarPlus size={15} aria-hidden /> Schedule shift
            </button>
            <Link to={`${base}/edit`} className="btn-secondary"><Pencil size={14} aria-hidden /> Edit</Link>
            <OverflowMenu items={menu} label={`More actions for ${name}`}
              className="!p-2.5 border border-slate-300 bg-white !text-slate-600 hover:!bg-slate-50 !rounded-xl" />
          </div>
        </div>

        <div className="flex items-center gap-4 mt-3">
          <PersonAvatar id={client.id} photo={client.profile_photo_url} firstName={client.first_name} lastName={client.last_name} size="lg" />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-tight tracking-[-0.01em] text-slate-900 truncate">{name}</h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <StatusPill status={archived ? 'archived' : 'active'} />
              {client.client_type && <span className="badge-gray whitespace-nowrap">{clientTypeLabel(client.client_type)}</span>}
              {sil?.method === 'weekly' && (
                <span className="whitespace-nowrap rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-800"
                  title={`SIL is claimed weekly from ${sil.from.split('-').reverse().join('/')}${sil.amount ? `, ${money(sil.amount)} a week` : ''}. SIL shifts show $0.00.`}>
                  SIL weekly
                </span>
              )}
              {sil?.method === 'hourly' && <span className="badge-gray whitespace-nowrap" title="Each SIL shift is claimed by the hour">SIL hourly</span>}
              {riskScore?.level === 'red' && (
                <span className="badge-red whitespace-nowrap" title={riskScore.flags?.join(' · ')}>
                  <AlertTriangle size={11} aria-hidden /> High risk
                </span>
              )}
              {riskScore?.level === 'yellow' && (
                <span className="badge-yellow whitespace-nowrap" title={riskScore.flags?.join(' · ')}>
                  <AlertTriangle size={11} aria-hidden /> At risk
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
          <StatRow cols={4}>
            <Fact label="NDIS number">{client.ndis_number || <NotSet />}</Fact>
            <Fact label="Date of birth">
              {client.date_of_birth ? (
                <>{fmtDate(dayOf(client.date_of_birth))}{age != null && <span className="ml-1 text-sm font-normal text-slate-500">({age})</span>}</>
              ) : <NotSet />}
            </Fact>
            <Fact label="Next review">
              {client.next_review_date ? (
                <span className="inline-flex items-center gap-1.5">
                  {fmtDate(dayOf(client.next_review_date))}
                  {review != null && review < 0 && <span className="badge-red !text-[11px]">Overdue</span>}
                  {review != null && review >= 0 && review <= 7 && <span className="badge-yellow !text-[11px]">This week</span>}
                </span>
              ) : <NotSet />}
            </Fact>
            <Fact label="Fund balance">
              {totalFunds > 0 ? (
                <>
                  {money(totalBalance)}
                  <span className="block text-[12px] font-normal text-slate-500">of {money(totalFunds)} allocated</span>
                </>
              ) : <span className="text-sm font-normal text-slate-500">No funds</span>}
            </Fact>
          </StatRow>
        </div>

        {primary && (
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-3 text-[13px] text-slate-600">
            <span className="text-slate-500">Primary contact:</span>
            <span className="font-semibold text-slate-800">{`${primary.first_name || ''} ${primary.last_name || ''}`.trim() || 'Unnamed'}</span>
            {primary.relation_category && <span className="text-slate-500">· {primary.relation_category}</span>}
            {primary.phone && (
              <>
                <span className="text-slate-400" aria-hidden>·</span>
                <a href={`tel:${String(primary.phone).replace(/\s+/g, '')}`} className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline">
                  <Phone size={12} aria-hidden /> {primary.phone}
                </a>
              </>
            )}
          </p>
        )}
      </div>

      <nav aria-label="Client record" className="mt-3 border-t border-slate-100 px-2 sm:px-3 print:hidden">
        <div className="flex items-end gap-0.5 overflow-x-auto snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,#000_calc(100%_-_2rem),transparent)] [-webkit-mask-image:linear-gradient(to_right,#000_calc(100%_-_2rem),transparent)] md:[mask-image:none] md:[-webkit-mask-image:none]">
          {TABS.map((t) => {
            const on = isTabActive(t);
            return (
              <Link key={t.label} to={base + t.path} aria-current={on ? 'page' : undefined}
                className={clsx(TAB_LINK, 'snap-start', on ? 'border-primary-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300')}>
                {t.label}
              </Link>
            );
          })}
          <OverflowMenu
            label="More client pages" icon={MoreTrigger}
            items={more.map((t) => ({ label: t.label, onClick: () => navigate(base + (t.to || t.path)) }))}
            className="-mb-px shrink-0 !p-0 !rounded-none !bg-transparent hover:!bg-transparent !text-inherit" />
        </div>
      </nav>

      <HistoryDialog clientId={client.id} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </header>
  );
}
